/**
 * M4: TARGET MENU — a Tab-toggled overlay listing every targetable object grouped
 * by category (stars / planets / moons / structures / stations / signals / contacts)
 * so the player can pick a nav-target quickly instead of blind-cycling. Pointer-
 * events:auto (like the services menu) so its rows are clickable; the rest of the
 * HUD stays click-through. Data is supplied per-open by main.ts (live distances +
 * select callbacks); this class is pure presentation + click routing.
 */
import './targetMenu.css';

/** One selectable row: a label, a sub-line (e.g. distance/kind), and its action. */
export interface TargetItem {
  label: string;
  sub: string;
  select: () => void;
}

/** A titled category of rows (empty groups are omitted). */
export interface TargetGroup {
  title: string;
  items: TargetItem[];
}

export class TargetMenu {
  private readonly root: HTMLDivElement;
  private open = false;
  private flat: TargetItem[] = []; // index-keyed for click delegation

  constructor() {
    this.root = document.createElement('div');
    this.root.id = 'target-menu';
    this.root.style.display = 'none';
    document.body.appendChild(this.root);
    this.root.addEventListener('click', (e) => this.onClick(e));
  }

  get isOpen(): boolean {
    return this.open;
  }

  /** Toggle the menu; when opening, render the supplied groups. */
  toggle(groups: TargetGroup[]): void {
    if (this.open) this.hide();
    else this.show(groups);
  }

  hide(): void {
    this.root.style.display = 'none';
    this.open = false;
  }

  private show(groups: TargetGroup[]): void {
    this.flat = [];
    let html = '<div class="tm-head">SELECT TARGET<button class="tm-close" data-close>CLOSE [TAB]</button></div><div class="tm-cols">';
    for (const g of groups) {
      if (g.items.length === 0) continue;
      html += `<div class="tm-group"><h3>${g.title}</h3>`;
      for (const it of g.items) {
        const idx = this.flat.length;
        this.flat.push(it);
        html += `<button class="tm-row" data-ti="${idx}"><span class="tm-name">${it.label}</span><span class="tm-sub">${it.sub}</span></button>`;
      }
      html += '</div>';
    }
    html += '</div>';
    this.root.innerHTML = html;
    this.root.style.display = 'block';
    this.open = true;
  }

  private onClick(e: MouseEvent): void {
    const el = (e.target as HTMLElement).closest('[data-ti],[data-close]') as HTMLElement | null;
    if (!el) return;
    if (el.hasAttribute('data-close')) {
      this.hide();
      return;
    }
    const idx = Number(el.getAttribute('data-ti'));
    const item = this.flat[idx];
    if (item) item.select();
    this.hide();
  }
}
